/**
 * Twilio API Helper
 * Provides typed methods for all Twilio API operations.
 * Ported from twilio-things.js UA_TPA_FEATURES API calls.
 */

import type { APIResponse } from '@/types/common';
import { proxyRequest } from '@/lib/api/proxy';
import {
  TWILIO_API_BASE,
  TWILIO_MESSAGING_V1,
  TWILIO_MESSAGING_V2,
  TWILIO_CONTENT_API,
  TWILIO_VALIDATE_URL,
  type TwilioAccount,
  type TwilioPhoneNumber,
  type TwilioWhatsAppSender,
  type TwilioContentTemplate,
  type TwilioMessage,
  type TwilioMessagingService,
  type TwilioServicePhoneNumber,
  type TwilioChannelSender,
  type TwilioTwiMLApp,
  type TwilioCallResource,
  type TwilioRecordingResource,
} from './twilio-types';

export class TwilioAPI {
  private extensionName: string;

  constructor(extensionName: string) {
    this.extensionName = extensionName;
  }

  // ── Generic API call (goes through Cloud Function proxy) ─────────

  private async apiCall<T = unknown>(
    url: string,
    method: string,
    data?: unknown,
    skipCache?: boolean
  ): Promise<APIResponse<T>> {
    // Extract TPA service name from extensionName (e.g. "twilioforzohocrm" → "twilio")
    const tpaService = this.extensionName.includes('for')
      ? this.extensionName.split('for')[0]
      : this.extensionName;

    return proxyRequest<T>({
      appCode: this.extensionName,
      url,
      method,
      data,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      TPAService: tpaService,
      skipCache,
    });
  }

  /** Like apiCall but sends JSON body instead of form-encoded.
   *  Required for Messaging V2 endpoints that expect nested JSON objects. */
  private async apiCallJSON<T = unknown>(
    url: string,
    method: string,
    data?: unknown
  ): Promise<APIResponse<T>> {
    const tpaService = this.extensionName.includes('for')
      ? this.extensionName.split('for')[0]
      : this.extensionName;

    return proxyRequest<T>({
      appCode: this.extensionName,
      url,
      method,
      data,
      headers: { 'Content-Type': 'application/json' },
      TPAService: tpaService,
    });
  }

  // ── Accounts ─────────────────────────────────────────────────────

  async getAccounts(): Promise<APIResponse<{ accounts: TwilioAccount[] }>> {
    return this.apiCall<{ accounts: TwilioAccount[] }>(
      `${TWILIO_API_BASE}/Accounts.json`,
      'GET_CACHED_ONE_WEEK'
    );
  }

  /**
   * Uncached version of getAccounts — used for auth checks so we always
   * get a fresh response from the server (bypasses cache).
   */
  async getAccountsUncached(): Promise<APIResponse<{ accounts: TwilioAccount[] }>> {
    return this.apiCall<{ accounts: TwilioAccount[] }>(
      `${TWILIO_API_BASE}/Accounts.json`,
      'GET'
    );
  }

  async getAccountInfo(
    sid: string
  ): Promise<APIResponse<TwilioAccount>> {
    return this.apiCall<TwilioAccount>(
      `${TWILIO_API_BASE}/Accounts/${sid}.json`,
      'GET_CACHED_ONE_WEEK'
    );
  }

  // ── Phone Numbers ────────────────────────────────────────────────

  async getIncomingPhoneNumbers(
    accountSid: string,
    pageSize: number = 70
  ): Promise<
    APIResponse<{ incoming_phone_numbers: TwilioPhoneNumber[] }>
  > {
    return this.apiCall<{ incoming_phone_numbers: TwilioPhoneNumber[] }>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/IncomingPhoneNumbers.json?PageSize=${pageSize}`,
      'GET_CACHED_ONE_DAY'
    );
  }

  // ── WhatsApp Senders ─────────────────────────────────────────────

  async getWhatsAppSenders(): Promise<
    APIResponse<{ senders: TwilioWhatsAppSender[] }>
  > {
    return this.apiCall<{ senders: TwilioWhatsAppSender[] }>(
      `${TWILIO_MESSAGING_V2}/Channels/Senders?Channel=WhatsApp`,
      'GET_CACHED_ONE_DAY'
    );
  }

  // ── Content Templates ────────────────────────────────────────────

  /**
   * List content templates *with* their WhatsApp approval state.
   *
   * Deliberately ContentAndApprovals rather than plain /Content: the latter
   * omits `approval_requests`, which left every template looking sendable.
   * An unapproved template sent outside the 24h service window is delivered
   * as a freeform message and rejected with error 63016, so the approval
   * state is what decides whether a template is usable at all.
   * Same response envelope, so callers are unaffected.
   */
  async getContentTemplates(
    pageSize: number = 99
  ): Promise<APIResponse<{ contents: TwilioContentTemplate[] }>> {
    return this.apiCall<{ contents: TwilioContentTemplate[] }>(
      `${TWILIO_CONTENT_API}/ContentAndApprovals?PageSize=${pageSize}`,
      'GET_CACHED_ONE_HOUR'
    );
  }

  // ── Messaging ────────────────────────────────────────────────────

  async sendMessage(
    accountSid: string,
    payload: Record<string, string>
  ): Promise<APIResponse<TwilioMessage>> {
    return this.apiCall<TwilioMessage>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Messages.json`,
      'POST',
      payload
    );
  }

  // ── Messages ─────────────────────────────────────────────────────

  async getMessages(
    accountSid: string,
    params: string
  ): Promise<
    APIResponse<{
      messages: TwilioMessage[];
      next_page_uri?: string;
    }>
  > {
    return this.apiCall<{
      messages: TwilioMessage[];
      next_page_uri?: string;
    }>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Messages.json?${params}`,
      'GET'
    );
  }

  async getMessagesByUrl(
    nextPageUri: string
  ): Promise<
    APIResponse<{
      messages: TwilioMessage[];
      next_page_uri?: string;
    }>
  > {
    const url = nextPageUri.startsWith('http')
      ? nextPageUri
      : `https://api.twilio.com${nextPageUri}`;
    return this.apiCall<{
      messages: TwilioMessage[];
      next_page_uri?: string;
    }>(url, 'GET');
  }

  async getMessageById(
    accountSid: string,
    messageSid: string
  ): Promise<APIResponse<TwilioMessage>> {
    return this.apiCall<TwilioMessage>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Messages/${messageSid}.json`,
      'GET'
    );
  }

  /**
   * Cancel a scheduled message by updating its status to 'canceled'.
   * Only works for messages with status 'scheduled'.
   */
  async cancelMessage(
    accountSid: string,
    messageSid: string
  ): Promise<APIResponse<TwilioMessage>> {
    return this.apiCall<TwilioMessage>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Messages/${messageSid}.json`,
      'POST',
      { Status: 'canceled' }
    );
  }

  // ── Messaging Services ───────────────────────────────────────────

  /**
   * List Messaging Services. Twilio returns ONE page, and its own default is
   * only 50 rows. On a bigger account every caller that looks for "our"
   * service missed it, created another empty one and sent without a
   * MessagingServiceSid. So the default here is Twilio's maximum page (1000).
   */
  async getMessagingServices(pageSize: number = 1000): Promise<
    APIResponse<{ services: TwilioMessagingService[] }>
  > {
    return this.apiCall<{ services: TwilioMessagingService[] }>(
      `${TWILIO_MESSAGING_V1}/Services?PageSize=${pageSize}`,
      'GET'
    );
  }

  /** Fetch ONE Messaging Service by SID — no list paging involved. */
  async getMessagingService(
    serviceSid: string
  ): Promise<APIResponse<TwilioMessagingService>> {
    return this.apiCall<TwilioMessagingService>(
      `${TWILIO_MESSAGING_V1}/Services/${serviceSid}`,
      'GET',
      undefined,
      true
    );
  }

  async createMessagingService(
    data: Record<string, string>
  ): Promise<APIResponse<TwilioMessagingService>> {
    return this.apiCall<TwilioMessagingService>(
      `${TWILIO_MESSAGING_V1}/Services`,
      'POST',
      data
    );
  }

  /** Update fields on a Messaging Service (e.g. blank its webhook URLs). */
  async updateMessagingService(
    serviceSid: string,
    data: Record<string, string>
  ): Promise<APIResponse<TwilioMessagingService>> {
    return this.apiCall<TwilioMessagingService>(
      `${TWILIO_MESSAGING_V1}/Services/${serviceSid}`,
      'POST',
      data
    );
  }

  async deleteMessagingService(
    serviceSid: string
  ): Promise<APIResponse<void>> {
    return this.apiCall<void>(
      `${TWILIO_MESSAGING_V1}/Services/${serviceSid}`,
      'DELETE'
    );
  }

  // ── Service Phone Numbers ────────────────────────────────────────

  async getServicePhoneNumbers(
    serviceSid: string
  ): Promise<
    APIResponse<{ phone_numbers: TwilioServicePhoneNumber[] }>
  > {
    return this.apiCall<{ phone_numbers: TwilioServicePhoneNumber[] }>(
      `${TWILIO_MESSAGING_V1}/Services/${serviceSid}/PhoneNumbers`,
      'GET_CACHED_ONE_DAY'
    );
  }

  async getServiceChannelSenders(
    serviceSid: string
  ): Promise<APIResponse<{ senders: TwilioChannelSender[] }>> {
    return this.apiCall<{ senders: TwilioChannelSender[] }>(
      `${TWILIO_MESSAGING_V1}/Services/${serviceSid}/ChannelSenders`,
      'GET_CACHED_ONE_DAY'
    );
  }

  async addPhoneNumberToService(
    serviceSid: string,
    phoneNumberSid: string
  ): Promise<APIResponse<unknown>> {
    return this.apiCall(
      `${TWILIO_MESSAGING_V1}/Services/${serviceSid}/PhoneNumbers`,
      'POST',
      { PhoneNumberSid: phoneNumberSid }
    );
  }

  async addChannelSenderToService(
    serviceSid: string,
    senderSid: string
  ): Promise<APIResponse<unknown>> {
    return this.apiCall(
      `${TWILIO_MESSAGING_V1}/Services/${serviceSid}/ChannelSenders`,
      'POST',
      { Sid: senderSid }
    );
  }

  /**
   * Remove a phone number from a messaging service
   */
  async removePhoneNumberFromService(
    serviceSid: string,
    phoneNumberSid: string
  ): Promise<APIResponse<unknown>> {
    return this.apiCall(
      `${TWILIO_MESSAGING_V1}/Services/${serviceSid}/PhoneNumbers/${phoneNumberSid}`,
      'DELETE'
    );
  }

  /**
   * Remove a channel sender (e.g. WhatsApp) from a messaging service
   */
  async removeChannelSenderFromService(
    serviceSid: string,
    senderSid: string
  ): Promise<APIResponse<unknown>> {
    return this.apiCall(
      `${TWILIO_MESSAGING_V1}/Services/${serviceSid}/ChannelSenders/${senderSid}`,
      'DELETE'
    );
  }

  /**
   * Get service phone numbers without cache (for real-time status)
   */
  async getServicePhoneNumbersUncached(
    serviceSid: string
  ): Promise<
    APIResponse<{ phone_numbers: TwilioServicePhoneNumber[] }>
  > {
    // skipCache — shares a cache key with the GET_CACHED_ONE_DAY sibling;
    // "Uncached" must reflect Twilio, not a day-old entry.
    return this.apiCall<{ phone_numbers: TwilioServicePhoneNumber[] }>(
      `${TWILIO_MESSAGING_V1}/Services/${serviceSid}/PhoneNumbers`,
      'GET',
      undefined,
      true
    );
  }

  /**
   * Get service channel senders without cache (for real-time status)
   */
  async getServiceChannelSendersUncached(
    serviceSid: string
  ): Promise<APIResponse<{ senders: TwilioChannelSender[] }>> {
    // skipCache — same shared-cache-key trap as above.
    return this.apiCall<{ senders: TwilioChannelSender[] }>(
      `${TWILIO_MESSAGING_V1}/Services/${serviceSid}/ChannelSenders`,
      'GET',
      undefined,
      true
    );
  }

  /**
   * Get incoming phone numbers without cache (for real-time status)
   */
  async getIncomingPhoneNumbersUncached(
    accountSid: string,
    pageSize: number = 70
  ): Promise<
    APIResponse<{ incoming_phone_numbers: TwilioPhoneNumber[] }>
  > {
    // skipCache — same URL may be in the proxy cache from a CACHED variant;
    // link state must always reflect Twilio, not a stale entry.
    return this.apiCall<{ incoming_phone_numbers: TwilioPhoneNumber[] }>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/IncomingPhoneNumbers.json?PageSize=${pageSize}`,
      'GET',
      undefined,
      true
    );
  }

  /**
   * Get WhatsApp senders without cache
   */
  async getWhatsAppSendersUncached(): Promise<
    APIResponse<{ senders: TwilioWhatsAppSender[] }>
  > {
    // skipCache — this URL is also fetched via GET_CACHED_ONE_DAY
    // (getWhatsAppSenders); without the bypass, the shared cache key serves
    // that day-old entry and link/unlink state appears to revert on reload.
    return this.apiCall<{ senders: TwilioWhatsAppSender[] }>(
      `${TWILIO_MESSAGING_V2}/Channels/Senders?Channel=WhatsApp`,
      'GET',
      undefined,
      true
    );
  }

  // ── Message Media ────────────────────────────────────────────────

  async getMessageMedia(
    mediaUri: string
  ): Promise<
    APIResponse<{
      media_list: Array<{ uri: string; content_type: string; sid: string }>;
    }>
  > {
    // mediaUri may be a full URL or a relative path
    const url = mediaUri.startsWith('http')
      ? mediaUri
      : `https://api.twilio.com${mediaUri}`;
    return this.apiCall(url, 'GET');
  }

  /** Update a phone number's configuration (e.g. VoiceApplicationSid) */
  async updatePhoneNumber(
    accountSid: string,
    phoneNumberSid: string,
    data: Record<string, string>
  ): Promise<APIResponse<TwilioPhoneNumber>> {
    return this.apiCall<TwilioPhoneNumber>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/IncomingPhoneNumbers/${phoneNumberSid}.json`,
      'POST',
      data
    );
  }

  // ── Voice / TwiML ───────────────────────────────────────────────

  /**
   * Get a voice access token for the Twilio Device.
   * This is a special Cloud Function action (not a proxied HTTP request).
   */
  async getVoiceToken(
    twiMLAppId: string
  ): Promise<APIResponse<{ token: string }>> {
    return this.apiCall<{ token: string }>(
      'TWILIO_VOICE_GET_UNIQUE_TOKEN',
      'POST',
      { voiceTwiMLAppId: twiMLAppId }
    );
  }

  /** Get TwiML applications for the account */
  async getTwiMLApps(
    accountSid: string
  ): Promise<APIResponse<{ applications: TwilioTwiMLApp[] }>> {
    return this.apiCall<{ applications: TwilioTwiMLApp[] }>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Applications.json`,
      'GET'
    );
  }

  /** Create a new TwiML application */
  async createTwiMLApp(
    accountSid: string,
    data: Record<string, string>
  ): Promise<APIResponse<TwilioTwiMLApp>> {
    return this.apiCall<TwilioTwiMLApp>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Applications`,
      'POST',
      data
    );
  }

  /** Update an existing TwiML application (e.g. fix VoiceUrl) */
  async updateTwiMLApp(
    accountSid: string,
    appSid: string,
    data: Record<string, string>
  ): Promise<APIResponse<TwilioTwiMLApp>> {
    return this.apiCall<TwilioTwiMLApp>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Applications/${appSid}.json`,
      'POST',
      data
    );
  }

  /** Get call history (paginated) */
  async getCalls(
    accountSid: string,
    params: string
  ): Promise<
    APIResponse<{ calls: TwilioCallResource[]; next_page_uri?: string }>
  > {
    return this.apiCall<{
      calls: TwilioCallResource[];
      next_page_uri?: string;
    }>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Calls.json?${params}`,
      'GET'
    );
  }

  /** Follow a pagination URL for call history */
  async getCallsByUrl(
    url: string
  ): Promise<
    APIResponse<{ calls: TwilioCallResource[]; next_page_uri?: string }>
  > {
    const fullUrl = url.startsWith('http')
      ? url
      : `https://api.twilio.com${url}`;
    return this.apiCall<{
      calls: TwilioCallResource[];
      next_page_uri?: string;
    }>(fullUrl, 'GET');
  }

  /** Get a single call's details */
  async getCallById(
    accountSid: string,
    callSid: string
  ): Promise<APIResponse<TwilioCallResource>> {
    return this.apiCall<TwilioCallResource>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Calls/${callSid}.json`,
      'GET'
    );
  }

  /** Get child calls for a parent call (for linking) */
  async getChildCalls(
    accountSid: string,
    parentCallSid: string
  ): Promise<APIResponse<{ calls: TwilioCallResource[] }>> {
    return this.apiCall<{ calls: TwilioCallResource[] }>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Calls.json?ParentCallSid=${parentCallSid}`,
      'GET'
    );
  }

  /** Start, pause, or resume call recording */
  async manageRecording(
    accountSid: string,
    callSid: string,
    data: Record<string, string>,
    isExistingRecording: boolean
  ): Promise<APIResponse<unknown>> {
    const url = `${TWILIO_API_BASE}/Accounts/${accountSid}/Calls/${callSid}/Recordings${isExistingRecording ? '/Twilio.CURRENT' : ''}.json`;
    return this.apiCall(url, 'POST', data);
  }

  /** Get recordings for a call */
  async getCallRecordings(
    accountSid: string,
    callSid: string
  ): Promise<
    APIResponse<{ recordings: TwilioRecordingResource[] }>
  > {
    return this.apiCall<{ recordings: TwilioRecordingResource[] }>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Calls/${callSid}/Recordings.json`,
      'GET'
    );
  }

  /** Get all recordings for the account (account-level, not call-level) */
  async getRecordings(
    accountSid: string,
    pageSize: number = 50
  ): Promise<
    APIResponse<{ recordings: TwilioRecordingResource[]; next_page_uri?: string }>
  > {
    return this.apiCall<{ recordings: TwilioRecordingResource[]; next_page_uri?: string }>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Recordings.json?PageSize=${pageSize}`,
      'GET'
    );
  }

  /** Follow a pagination URL for recordings */
  async getRecordingsByUrl(
    url: string
  ): Promise<
    APIResponse<{ recordings: TwilioRecordingResource[]; next_page_uri?: string }>
  > {
    const fullUrl = url.startsWith('http')
      ? url
      : `https://api.twilio.com${url}`;
    return this.apiCall<{ recordings: TwilioRecordingResource[]; next_page_uri?: string }>(
      fullUrl,
      'GET'
    );
  }

  /**
   * Update an active call — used for cold transfer.
   * Sets a new TwiML URL that redirects the call to a different target.
   */
  async updateCall(
    accountSid: string,
    callSid: string,
    params: string
  ): Promise<APIResponse<TwilioCallResource>> {
    return this.apiCall<TwilioCallResource>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Calls/${callSid}.json`,
      'POST',
      params
    );
  }

  // ── Queues ──────────────────────────────────────────────────────

  async getQueues(
    accountSid: string
  ): Promise<APIResponse<{ queues: Array<{ sid: string; friendly_name: string; current_size: number; average_wait_time: number; max_size: number }> }>> {
    return this.apiCall<{ queues: Array<{ sid: string; friendly_name: string; current_size: number; average_wait_time: number; max_size: number }> }>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Queues.json`,
      'GET'
    );
  }

  async getQueueMembers(
    accountSid: string,
    queueSid: string
  ): Promise<APIResponse<{ queue_members: Array<{ call_sid: string; date_enqueued: string; wait_time: number; position: number }> }>> {
    return this.apiCall<{ queue_members: Array<{ call_sid: string; date_enqueued: string; wait_time: number; position: number }> }>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Queues/${queueSid}/Members.json`,
      'GET'
    );
  }

  // ── Conferences (Whisper/Barge/Listen) ─────────────────────────

  /** List active conferences, optionally filtered to an exact friendly name
   *  (server-side filter — immune to pagination past 50 conferences) */
  async getConferences(
    accountSid: string,
    status = 'in-progress',
    friendlyName?: string
  ): Promise<APIResponse<{ conferences: Array<{ sid: string; friendly_name: string; status: string; date_created: string }> }>> {
    // Master's server-side name filter and this branch's pagination are both
    // wanted: the filter makes a targeted lookup exact, and pagination keeps
    // an unfiltered scan from silently missing a conference on a busy account.
    // `next_page_uri` carries the filter, so later pages stay narrowed too.
    const nameFilter = friendlyName ? `&FriendlyName=${encodeURIComponent(friendlyName)}` : '';
    type ConfPage = {
      conferences: Array<{ sid: string; friendly_name: string; status: string; date_created: string }>;
      next_page_uri?: string | null;
    };
    const conferences: ConfPage['conferences'] = [];
    let url: string | null =
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Conferences.json?Status=${status}${nameFilter}&PageSize=100`;
    let lastResp: APIResponse<ConfPage> | null = null;
    for (let page = 0; page < 5 && url; page++) {
      const resp: APIResponse<ConfPage> = await this.apiCall<ConfPage>(url, 'GET');
      lastResp = resp;
      if (!resp.success) break;
      conferences.push(...(resp.data?.conferences || []));
      url = resp.data?.next_page_uri
        ? `https://api.twilio.com${resp.data.next_page_uri}`
        : null;
    }
    // Hitting the cap means the listing is TRUNCATED, not complete — callers
    // scanning for a specific conference (transfer, note anchor) may simply
    // not see it. Surface that loudly; Status=in-progress lists every
    // conference on the ACCOUNT, so shared accounts can exceed this without
    // this app being busy at all.
    if (url) {
      console.warn(
        `[TwilioAPI] getConferences hit the 5-page cap — ${conferences.length} returned but more exist; scans over this listing are incomplete`
      );
    }
    return {
      success: lastResp?.success ?? false,
      error: lastResp?.error,
      statusCode: lastResp?.statusCode,
      data: { conferences },
    };
  }

  /** List participants in a conference */
  async getConferenceParticipants(
    accountSid: string,
    conferenceSid: string
  ): Promise<APIResponse<{ participants: Array<{ call_sid: string; label: string; muted: boolean; hold: boolean; coaching: boolean; status: string }> }>> {
    return this.apiCall<{ participants: Array<{ call_sid: string; label: string; muted: boolean; hold: boolean; coaching: boolean; status: string }> }>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Conferences/${conferenceSid}/Participants.json`,
      'GET'
    );
  }

  /**
   * Add a participant to a conference.
   * @param from - The caller ID to display
   * @param to - The participant to add (client:email or phone number)
   * @param params - Additional params: Label, Muted, Coaching, CallSidToCoach, Beep, etc.
   */
  async addConferenceParticipant(
    accountSid: string,
    conferenceSid: string,
    from: string,
    to: string,
    params?: string
  ): Promise<APIResponse<{ call_sid: string; label: string }>> {
    const body = `From=${encodeURIComponent(from)}&To=${encodeURIComponent(to)}${params ? '&' + params : ''}`;
    return this.apiCall<{ call_sid: string; label: string }>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Conferences/${conferenceSid}/Participants.json`,
      'POST',
      body
    );
  }

  /** Find an in-progress conference by its friendly name (fresh, no cache). */
  async getConferenceByName(
    accountSid: string,
    friendlyName: string
  ): Promise<APIResponse<{ conferences: Array<{ sid: string; status: string }> }>> {
    return this.apiCall<{ conferences: Array<{ sid: string; status: string }> }>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Conferences.json?FriendlyName=${encodeURIComponent(friendlyName)}&Status=in-progress&PageSize=5`,
      'GET',
      undefined,
      true
    );
  }

  /** Update a conference participant (mute, hold, coach, etc.) */
  async updateConferenceParticipant(
    accountSid: string,
    conferenceSid: string,
    callSid: string,
    params: string
  ): Promise<APIResponse<void>> {
    return this.apiCall<void>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Conferences/${conferenceSid}/Participants/${callSid}.json`,
      'POST',
      params
    );
  }

  /** Remove a participant from a conference */
  async removeConferenceParticipant(
    accountSid: string,
    conferenceSid: string,
    callSid: string
  ): Promise<APIResponse<void>> {
    return this.apiCall<void>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Conferences/${conferenceSid}/Participants/${callSid}.json`,
      'DELETE'
    );
  }

  // ── WhatsApp Business Calling ────────────────────────────────────

  /**
   * Set or remove voice_application_sid on a WhatsApp sender.
   * This enables/disables voice calling for the sender.
   * @see https://www.twilio.com/docs/voice/whatsapp-business-calling
   */
  async updateWhatsAppSenderVoiceConfig(
    senderSid: string,
    voiceAppSid: string | null
  ): Promise<APIResponse<TwilioWhatsAppSender>> {
    // Messaging V2 Senders endpoint expects application/json (not form-encoded).
    // Clearing the voice app uses an empty string — the Senders API treats
    // JSON null as "field omitted" and rejects the update with
    // "Update request body is empty" (verified against production).
    return this.apiCallJSON<TwilioWhatsAppSender>(
      `${TWILIO_MESSAGING_V2}/Channels/Senders/${senderSid}`,
      'POST',
      {
        configuration: {
          voice_application_sid: voiceAppSid ?? '',
        },
      }
    );
  }

  /**
   * Validate a phone number via Twilio Lookup v2 (basic tier = free format
   * validation). Pass countryCode for nationally-formatted input (no '+').
   * Returns data.valid plus the normalized E.164 in data.phone_number.
   */
  async lookupPhoneNumber(
    phoneNumber: string,
    countryCode?: string
  ): Promise<APIResponse<{ valid?: boolean; phone_number?: string }>> {
    const params = countryCode ? `?CountryCode=${encodeURIComponent(countryCode)}` : '';
    return this.apiCall<{ valid?: boolean; phone_number?: string }>(
      `https://lookups.twilio.com/v2/PhoneNumbers/${encodeURIComponent(phoneNumber)}${params}`,
      'GET',
      undefined,
      true
    );
  }

  /**
   * Get details for a specific WhatsApp sender (to check voice config).
   */
  async getWhatsAppSenderDetails(
    senderSid: string
  ): Promise<APIResponse<TwilioWhatsAppSender>> {
    return this.apiCall<TwilioWhatsAppSender>(
      `${TWILIO_MESSAGING_V2}/Channels/Senders/${senderSid}`,
      'GET'
    );
  }

  /**
   * Initiate an outbound WhatsApp voice call via Twilio REST API.
   * From/To must use whatsapp:+XXX format.
   * @see https://www.twilio.com/docs/voice/whatsapp-business-calling#outbound-calling
   */
  async createWhatsAppCall(
    accountSid: string,
    from: string,
    to: string,
    twimlUrl: string
  ): Promise<APIResponse<TwilioCallResource>> {
    return this.apiCall<TwilioCallResource>(
      `${TWILIO_API_BASE}/Accounts/${accountSid}/Calls.json`,
      'POST',
      { From: from, To: to, Url: twimlUrl }
    );
  }

  /**
   * Create a content template for WhatsApp voice call actions.
   * Used to create VOICE_CALL (inbound) and VOICE_CALL_REQUEST (outbound permission) templates.
   * @see https://www.twilio.com/docs/voice/whatsapp-business-calling#voice-call-button
   */
  async createContentTemplate(
    data: Record<string, unknown>
  ): Promise<APIResponse<TwilioContentTemplate>> {
    // Content API expects application/json
    return this.apiCallJSON<TwilioContentTemplate>(
      `${TWILIO_CONTENT_API}/Content`,
      'POST',
      data
    );
  }

  /**
   * Edit an existing content template in place, keeping its SID.
   *
   * Lets a caller that owns a ContentSid update it instead of creating a second
   * template every time the content changes. Only works while the template has
   * NOT been submitted for WhatsApp approval (submitted templates are locked,
   * and the language can never change) — callers should fall back to
   * `createContentTemplate` if this returns an error.
   * @see https://www.twilio.com/docs/content/content-api-resources
   */
  async updateContentTemplate(
    contentSid: string,
    data: Record<string, unknown>
  ): Promise<APIResponse<TwilioContentTemplate>> {
    return this.apiCallJSON<TwilioContentTemplate>(
      `${TWILIO_CONTENT_API}/Content/${contentSid}`,
      'PUT',
      data
    );
  }

  /**
   * Delete a content template.
   *
   * Used to clean up a template that had to be replaced rather than edited, so
   * the account doesn't accumulate orphans. Can fail for a template WhatsApp has
   * approved — callers should treat failure as non-fatal and surface the SID.
   */
  async deleteContentTemplate(
    contentSid: string
  ): Promise<APIResponse<unknown>> {
    return this.apiCall<unknown>(
      `${TWILIO_CONTENT_API}/Content/${contentSid}`,
      'DELETE'
    );
  }

  // ── Token Validation ─────────────────────────────────────────────

  /**
   * Validate Twilio credentials via the msbrequest Cloud Function.
   * Uses Basic Auth (SID:Token).
   * Returns 'valid', 'inactive' (Twilio 20003 "... is not active" — usually a
   * zero balance / suspended account), or 'invalid'.
   */
  async validateCredentials(
    accountSid: string,
    authToken: string
  ): Promise<'valid' | 'inactive' | 'invalid'> {
    try {
      const url = `${TWILIO_API_BASE}/Accounts/${accountSid}.json`;
      const response = await fetch(TWILIO_VALIDATE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json;charset=utf-8' },
        body: JSON.stringify({
          url,
          method: 'GET',
          headers: {
            Authorization:
              'Basic ' + btoa(`${accountSid}:${authToken}`),
          },
        }),
      });

      const result = await response.json();

      if (
        result.data ||
        result.status === 200 ||
        result.code === 200
      ) {
        return 'valid';
      }

      const errMessage = String(result.error?.message ?? result.message ?? '');
      if (/not active/i.test(errMessage)) {
        return 'inactive';
      }

      return 'invalid';
    } catch {
      return 'invalid';
    }
  }

  /**
   * Delete a Twilio Event Streams sink.
   * Returns the API response (204 on success, 404 if already gone).
   */
  async apiDeleteSink(sinkSid: string): Promise<APIResponse<unknown>> {
    const { TWILIO_EVENTS_API } = await import('./twilio-types');
    return this.apiCallJSON<unknown>(
      `${TWILIO_EVENTS_API}/Sinks/${sinkSid}`,
      'DELETE'
    );
  }

  /**
   * Delete a Twilio Event Streams subscription.
   * Use this instead of deleting the sink — sinks can be shared across orgs
   * and cannot be deleted while subscriptions exist.
   */
  async apiDeleteSubscription(subscriptionSid: string): Promise<APIResponse<unknown>> {
    const { TWILIO_EVENTS_API } = await import('./twilio-types');
    return this.apiCallJSON<unknown>(
      `${TWILIO_EVENTS_API}/Subscriptions/${subscriptionSid}`,
      'DELETE'
    );
  }

  /**
   * Fetch a single Event Streams subscription. The page-load happy path:
   * `apiGetSubscription(storedSubscriptionId)` → 200 means everything is
   * fine, no list calls needed. Only on 404 do we fall back to the heavier
   * list-and-discover flow.
   *
   * Uses `apiCallJSON` (Content-Type: application/json) — same convention as
   * `apiDeleteSubscription`. The form-urlencoded path in the proxy CF builds
   * a `new URLSearchParams(undefined)` body that axios sends on GETs, and
   * Twilio's Events endpoints respond unpredictably to GETs with a body.
   */
  async apiGetSubscription(
    subscriptionSid: string
  ): Promise<APIResponse<{ sid: string; sink_sid: string; status: string }>> {
    const { TWILIO_EVENTS_API } = await import('./twilio-types');
    return this.apiCallJSON<{ sid: string; sink_sid: string; status: string }>(
      `${TWILIO_EVENTS_API}/Subscriptions/${subscriptionSid}`,
      'GET'
    );
  }

  /**
   * List all Event Streams sinks on the Twilio account. Fallback for when
   * the stored subscription_id 404s — we discover the Ulgebra-owned sink
   * by its webhook destination domain instead of trusting stale prefs.
   */
  async apiListSinks(): Promise<
    APIResponse<{ sinks: Array<{ sid: string; sink_configuration?: { destination?: string } }> }>
  > {
    const { TWILIO_EVENTS_API } = await import('./twilio-types');
    return this.apiCallJSON<{ sinks: Array<{ sid: string; sink_configuration?: { destination?: string } }> }>(
      `${TWILIO_EVENTS_API}/Sinks`,
      'GET'
    );
  }

  /**
   * List all Event Streams subscriptions on the Twilio account. The client
   * filters this list by `sink_sid` to find the live subscription on our
   * sink — more reliable than trusting a stored subscription_id which may
   * have drifted (e.g. someone re-created the subscription via Twilio console).
   */
  async apiListSubscriptions(): Promise<
    APIResponse<{ subscriptions: Array<{ sid: string; sink_sid: string; status?: string }> }>
  > {
    const { TWILIO_EVENTS_API } = await import('./twilio-types');
    return this.apiCallJSON<{ subscriptions: Array<{ sid: string; sink_sid: string; status?: string }> }>(
      `${TWILIO_EVENTS_API}/Subscriptions`,
      'GET'
    );
  }

  /**
   * Create a new Event Streams subscription on a sink, subscribed to the
   * messaging event types we care about.
   *
   * Twilio expects each event type as a separate `Types` parameter with a
   * JSON-stringified object — not a single array parameter. The proxy's
   * URLSearchParams constructor handles a `[key, value]` tuple array as
   * repeating keys, so that's the shape we pass.
   */
  async apiCreateSubscription(
    sinkSid: string,
    description: string,
    ignoredTypes: string[] = []
  ): Promise<APIResponse<{ sid: string; sink_sid: string }>> {
    const { TWILIO_EVENTS_API, TWILIO_INBOUND_MESSAGE_EVENT, TWILIO_MESSAGE_EVENTS } = await import('./twilio-types');
    // Single source of truth: the same list rendered as toggles in settings,
    // so the subscription's event set can't drift from the UI.
    const allEventTypeNames = TWILIO_MESSAGE_EVENTS.map((e) => e.id);
    // Subscribe to every event the admin hasn't disabled. If they disabled
    // everything, keep the inbound-message event so the subscription still has
    // at least one type — Twilio rejects an empty subscription. (Turning off
    // all events is really "disable the sink", done via the sink toggle.)
    const enabled = allEventTypeNames.filter((t) => !ignoredTypes.includes(t));
    const eventTypeNames = enabled.length > 0 ? enabled : [TWILIO_INBOUND_MESSAGE_EVENT];
    const body: Array<[string, string]> = [
      ['Description', description],
      ['SinkSid', sinkSid],
      ...eventTypeNames.map((t) => ['Types', JSON.stringify({ type: t })] as [string, string]),
    ];
    return this.apiCall<{ sid: string; sink_sid: string }>(
      `${TWILIO_EVENTS_API}/Subscriptions`,
      'POST',
      body
    );
  }

  /**
   * Add a single event type to an existing subscription's SubscribedEvents.
   * Used when a user re-enables a delivery status.
   *   POST /v1/Subscriptions/{Sid}/SubscribedEvents   (form field: Type)
   */
  async apiAddSubscribedEvent(
    subscriptionSid: string,
    type: string
  ): Promise<APIResponse<unknown>> {
    const { TWILIO_EVENTS_API } = await import('./twilio-types');
    return this.apiCall<unknown>(
      `${TWILIO_EVENTS_API}/Subscriptions/${subscriptionSid}/SubscribedEvents`,
      'POST',
      [['Type', type]] as Array<[string, string]>
    );
  }

  /**
   * Remove a single event type from an existing subscription's
   * SubscribedEvents. Used when a user disables a delivery status.
   *   DELETE /v1/Subscriptions/{Sid}/SubscribedEvents/{Type}
   */
  async apiRemoveSubscribedEvent(
    subscriptionSid: string,
    type: string
  ): Promise<APIResponse<unknown>> {
    const { TWILIO_EVENTS_API } = await import('./twilio-types');
    return this.apiCallJSON<unknown>(
      `${TWILIO_EVENTS_API}/Subscriptions/${subscriptionSid}/SubscribedEvents/${type}`,
      'DELETE'
    );
  }
}
